import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/portal/widgets";
import api, { inr } from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["requested", "confirmed", "ready", "fulfilled", "cancelled"];

function csv(rows) {
  const head = ["Reference", "Customer", "Store", "Fulfilment", "Total", "Status"];
  const body = rows.map((o) => [o.reference, o.customer_name, o.store_name, o.fulfilment, o.total, o.status]);
  const content = [head, ...body].map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
  const blob = new Blob([content], { type: "text/csv" });
  const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "orders.csv"; a.click();
}

export default function OrdersAdmin() {
  const [orders, setOrders] = useState([]);
  const load = () => api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    await api.patch(`/admin/orders/${id}`, { status });
    toast.success("Order updated"); load();
  };

  return (
    <div className="animate-fade-up">
      <PageHeader title="Order Requests" subtitle={`${orders.length} order requests across all stores.`}
        action={<Button variant="outline" onClick={() => csv(orders)} data-testid="export-orders-btn"><Download className="w-4 h-4 mr-1" /> Export CSV</Button>} />

      <Card className="border-slate-200/80 dark:border-slate-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-muted-foreground bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
              <th className="py-3 px-4">Reference</th><th className="py-3 px-4">Customer</th><th className="py-3 px-4">Store</th><th className="py-3 px-4">Type</th><th className="py-3 px-4">Items</th><th className="py-3 px-4">Total</th><th className="py-3 px-4">Status</th>
            </tr></thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} data-testid={`order-row-${o.id}`} className="border-b border-slate-100 dark:border-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  <td className="py-2.5 px-4 font-mono text-xs">{o.reference}</td>
                  <td className="py-2.5 px-4 font-medium">{o.customer_name}</td>
                  <td className="py-2.5 px-4 text-muted-foreground">{o.store_name}</td>
                  <td className="py-2.5 px-4"><Badge variant="secondary" className="capitalize">{o.fulfilment}</Badge></td>
                  <td className="py-2.5 px-4 text-muted-foreground">{o.items?.length || 0}</td>
                  <td className="py-2.5 px-4 font-semibold">{inr(o.total)}</td>
                  <td className="py-2.5 px-4">
                    <Select value={o.status} onValueChange={(v) => setStatus(o.id, v)}>
                      <SelectTrigger data-testid={`order-status-${o.id}`} className="h-8 w-32 capitalize"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
